import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Sparkles, FileText, Target, TrendingUp, 
  Users, DollarSign, AlertTriangle, CheckCircle, Loader2,
  RefreshCw, Briefcase, LineChart, Shield, Building2
} from "lucide-react";

export const BusinessPlanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: "",
    industry: "",
    business_model: "",
    target_market: "",
    products_services: "",
    unique_value_proposition: "",
    startup_costs: "",
    monthly_expenses: "",
    revenue_streams: "",
    short_term_goals: "",
    long_term_vision: ""
  });

  useEffect(() => {
    fetchPlan();
  }, []);

  const fetchPlan = async () => {
    try {
      const response = await authFetch(`${API}/business-plan`, {  });
      if (response.ok) {
        const data = await response.json();
        setPlan(data);
        if (data?.input_data) {
          setFormData({
            business_name: data.input_data.business_name || "",
            industry: data.input_data.industry || "",
            business_model: data.input_data.business_model || "",
            target_market: data.input_data.target_market || "",
            products_services: data.input_data.products_services || "",
            unique_value_proposition: data.input_data.unique_value_proposition || "",
            startup_costs: data.input_data.startup_costs?.toString() || "",
            monthly_expenses: data.input_data.monthly_expenses?.toString() || "",
            revenue_streams: (data.input_data.revenue_streams || []).join(", "),
            short_term_goals: data.input_data.short_term_goals || "",
            long_term_vision: data.input_data.long_term_vision || ""
          });
        }
      }
    } catch (_error) {
      console.error("Error fetching plan:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.business_name || !formData.industry || !formData.business_model) {
      toast.error("Please fill in required fields");
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        business_name: formData.business_name,
        industry: formData.industry,
        business_model: formData.business_model,
        target_market: formData.target_market,
        products_services: formData.products_services,
        unique_value_proposition: formData.unique_value_proposition,
        startup_costs: parseFloat(formData.startup_costs) || 0,
        monthly_expenses: parseFloat(formData.monthly_expenses) || 0,
        revenue_streams: formData.revenue_streams.split(",").map(s => s.trim()).filter(Boolean),
        short_term_goals: formData.short_term_goals,
        long_term_vision: formData.long_term_vision
      };

      const response = await authFetch(`${API}/ai/generate-business-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setPlan({ generated_plan: data.plan, input_data: payload });
        setShowForm(false);
        toast.success("Business plan generated!");
      } else {
        toast.error("Failed to generate business plan");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const p = plan?.generated_plan;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="business-plan-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Business Plan</h1>
                <p className="text-sm text-neutral-500">AI-powered comprehensive plan</p>
              </div>
            </div>
            
            {plan && !showForm && (
              <Button onClick={() => setShowForm(true)} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Explanation Card */}
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 mb-8">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">AI Business Plan Generator</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Create a comprehensive business plan in minutes. Our AI analyzes your business details to generate 
                  an executive summary, market analysis, competitive positioning, marketing strategy, financial 
                  projections, and actionable milestones—perfect for planning, funding, or strategic alignment.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-indigo-200 text-indigo-700">Executive Summary</span>
                  <span className="px-2 py-1 bg-white rounded border border-indigo-200 text-indigo-700">Market Analysis</span>
                  <span className="px-2 py-1 bg-white rounded border border-indigo-200 text-indigo-700">Financial Projections</span>
                  <span className="px-2 py-1 bg-white rounded border border-indigo-200 text-indigo-700">Milestone Planning</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Input Form */}
        {(!plan || showForm) && (
          <Card className="border-neutral-200 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-emerald-500" />
                Generate Business Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Your Company Name"
                    data-testid="plan-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="e.g., Technology, Healthcare, Retail"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Model *</Label>
                <Input
                  value={formData.business_model}
                  onChange={(e) => setFormData({ ...formData, business_model: e.target.value })}
                  placeholder="e.g., B2B SaaS, E-commerce, Consulting, Subscription"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Target Market</Label>
                <Textarea
                  value={formData.target_market}
                  onChange={(e) => setFormData({ ...formData, target_market: e.target.value })}
                  placeholder="Describe your target customers and market..."
                  className="min-h-16"
                />
              </div>

              <div className="space-y-2">
                <Label>Products/Services</Label>
                <Textarea
                  value={formData.products_services}
                  onChange={(e) => setFormData({ ...formData, products_services: e.target.value })}
                  placeholder="Describe what you offer..."
                  className="min-h-16"
                />
              </div>

              <div className="space-y-2">
                <Label>Unique Value Proposition</Label>
                <Textarea
                  value={formData.unique_value_proposition}
                  onChange={(e) => setFormData({ ...formData, unique_value_proposition: e.target.value })}
                  placeholder="What makes your business unique?"
                  className="min-h-16"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Startup Costs ($)</Label>
                  <Input
                    type="number"
                    value={formData.startup_costs}
                    onChange={(e) => setFormData({ ...formData, startup_costs: e.target.value })}
                    placeholder="50000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estimated Monthly Expenses ($)</Label>
                  <Input
                    type="number"
                    value={formData.monthly_expenses}
                    onChange={(e) => setFormData({ ...formData, monthly_expenses: e.target.value })}
                    placeholder="10000"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Revenue Streams (comma-separated)</Label>
                <Input
                  value={formData.revenue_streams}
                  onChange={(e) => setFormData({ ...formData, revenue_streams: e.target.value })}
                  placeholder="e.g., Product sales, Subscriptions, Services, Consulting"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Short-term Goals (6 months)</Label>
                  <Textarea
                    value={formData.short_term_goals}
                    onChange={(e) => setFormData({ ...formData, short_term_goals: e.target.value })}
                    placeholder="What do you want to achieve in 6 months?"
                    className="min-h-16"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Long-term Vision (2+ years)</Label>
                  <Textarea
                    value={formData.long_term_vision}
                    onChange={(e) => setFormData({ ...formData, long_term_vision: e.target.value })}
                    placeholder="Where do you see the business in 2+ years?"
                    className="min-h-16"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                {showForm && (
                  <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                )}
                <Button 
                  onClick={handleGenerate} 
                  disabled={generating}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="generate-plan-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Business Plan
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generated Plan */}
        {p && !p.parse_error && !showForm && (
          <>
          {/* Cover Page */}
          {p.cover_page && (
            <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-white mb-6">
              <CardContent className="py-8 text-center">
                <h2 className="text-2xl font-bold text-neutral-900 mb-1">{p.cover_page.business_name}</h2>
                {p.cover_page.tagline && <p className="text-neutral-600 italic mb-4">{p.cover_page.tagline}</p>}
                <p className="text-sm text-emerald-600 font-medium">{p.cover_page.date}</p>
                <p className="text-xs text-neutral-400 mt-3">{p.cover_page.powered_by || "Powered by My Biz Consulting - Business Solutions Center"}</p>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="summary" className="space-y-6">
            <TabsList className="bg-white border flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="summary" className="text-xs">Executive Summary</TabsTrigger>
              <TabsTrigger value="company" className="text-xs">Company</TabsTrigger>
              <TabsTrigger value="opportunity" className="text-xs">Opportunity</TabsTrigger>
              <TabsTrigger value="market" className="text-xs">Market &amp; Industry</TabsTrigger>
              <TabsTrigger value="strategy" className="text-xs">Strategy</TabsTrigger>
              <TabsTrigger value="milestones" className="text-xs">Milestones</TabsTrigger>
              <TabsTrigger value="financial" className="text-xs">Financial</TabsTrigger>
              <TabsTrigger value="funding" className="text-xs">Funding</TabsTrigger>
            </TabsList>

            {/* Executive Summary */}
            <TabsContent value="summary">
              {p.executive_summary && (
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <FileText className="w-5 h-5 mr-2 text-emerald-500" />
                      Executive Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <h4 className="font-medium text-emerald-800 mb-2">Business Overview</h4>
                      <p className="text-sm text-neutral-700 whitespace-pre-line">{p.executive_summary.business_overview}</p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">Mission</h4>
                        <p className="text-sm text-neutral-700">{p.executive_summary.mission}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-medium text-purple-800 mb-2">Vision</h4>
                        <p className="text-sm text-neutral-700">{p.executive_summary.vision}</p>
                      </div>
                    </div>

                    {p.executive_summary.objectives?.length > 0 && (
                      <div className="p-4 bg-neutral-50 rounded-lg">
                        <h4 className="font-medium text-neutral-800 mb-2">Key Objectives</h4>
                        <ul className="space-y-1">
                          {p.executive_summary.objectives.map((obj, idx) => (
                            <li key={idx} className="text-sm text-neutral-700 flex items-start">
                              <Target className="w-4 h-4 mr-2 mt-0.5 text-emerald-500 flex-shrink-0" />
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {p.executive_summary.keys_to_success?.length > 0 && (
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <h4 className="font-medium text-amber-800 mb-2">Keys to Success</h4>
                        <ul className="space-y-1">
                          {p.executive_summary.keys_to_success.map((key, idx) => (
                            <li key={idx} className="text-sm text-neutral-700 flex items-start">
                              <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-amber-500 flex-shrink-0" />
                              {key}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Market Analysis */}
            <TabsContent value="market">
              {p.market_analysis && (
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Users className="w-5 h-5 mr-2 text-blue-500" />
                      Market Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <h4 className="font-medium text-neutral-800 mb-2">Industry Overview</h4>
                      <p className="text-sm text-neutral-700">{p.market_analysis.industry_overview}</p>
                    </div>

                    {p.market_analysis.target_market && (
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-medium text-blue-800 mb-2">Target Market</h4>
                          <p className="text-sm text-neutral-700">{p.market_analysis.target_market.description}</p>
                        </div>
                        <div className="p-4 bg-emerald-50 rounded-lg">
                          <h4 className="font-medium text-emerald-800 mb-2">Market Size</h4>
                          <p className="text-sm text-neutral-700">{p.market_analysis.target_market.size}</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <h4 className="font-medium text-purple-800 mb-2">Growth Rate</h4>
                          <p className="text-sm text-neutral-700">{p.market_analysis.target_market.growth_rate}</p>
                        </div>
                      </div>
                    )}

                    {p.market_analysis.competitive_analysis && (
                      <div className="p-4 bg-neutral-50 rounded-lg">
                        <h4 className="font-medium text-neutral-800 mb-3">Competitive Analysis</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-neutral-600 mb-1">Direct Competitors:</p>
                            <div className="flex flex-wrap gap-2">
                              {p.market_analysis.competitive_analysis.direct_competitors?.map((c, idx) => (
                                <Badge key={idx} variant="secondary">{c}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-600 mb-1">Your Competitive Advantages:</p>
                            <ul className="space-y-1">
                              {p.market_analysis.competitive_analysis.competitive_advantages?.map((adv, idx) => (
                                <li key={idx} className="text-sm text-neutral-700 flex items-start">
                                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                                  {adv}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-600 mb-1">Market Positioning:</p>
                            <p className="text-sm text-neutral-700">{p.market_analysis.competitive_analysis.market_positioning}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Operations */}
            <TabsContent value="operations">
              {p.operations_plan && (
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Briefcase className="w-5 h-5 mr-2 text-purple-500" />
                      Operations Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <h4 className="font-medium text-neutral-800 mb-2">Business Model</h4>
                      <p className="text-sm text-neutral-700">{p.operations_plan.business_model}</p>
                    </div>

                    {p.operations_plan.key_processes?.length > 0 && (
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">Key Processes</h4>
                        <ul className="space-y-1">
                          {p.operations_plan.key_processes.map((proc, idx) => (
                            <li key={idx} className="text-sm text-neutral-700">• {proc}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-medium text-purple-800 mb-2">Team Structure</h4>
                      <p className="text-sm text-neutral-700">{p.operations_plan.team_structure}</p>
                    </div>

                    {p.operations_plan.milestones?.length > 0 && (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <h4 className="font-medium text-emerald-800 mb-3">Milestones</h4>
                        <div className="space-y-2">
                          {p.operations_plan.milestones.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                              <span className="text-sm">{m.milestone}</span>
                              <Badge variant="secondary">{m.timeline}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Risk Analysis */}
              {p.risk_analysis && (
                <Card className="border-neutral-200 mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Shield className="w-5 h-5 mr-2 text-amber-500" />
                      Risk Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {p.risk_analysis.key_risks?.length > 0 && (
                      <div className="space-y-3">
                        {p.risk_analysis.key_risks.map((risk, idx) => (
                          <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                            <div className="flex items-start">
                              <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 text-amber-500 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-neutral-800">{risk.risk}</p>
                                <p className="text-sm text-neutral-600 mt-1">
                                  <span className="font-medium text-emerald-700">Mitigation:</span> {risk.mitigation}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Financial */}
            <TabsContent value="financial">
              {p.financial_projections && (
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <DollarSign className="w-5 h-5 mr-2 text-green-500" />
                      Financial Projections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Startup Costs */}
                    {p.financial_projections.startup_costs_breakdown?.length > 0 && (
                      <div className="p-4 bg-neutral-50 rounded-lg">
                        <h4 className="font-medium text-neutral-800 mb-3">Startup Costs Breakdown</h4>
                        <div className="space-y-2">
                          {p.financial_projections.startup_costs_breakdown.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-neutral-600">{item.item}</span>
                              <span className="font-medium">{formatCurrency(item.amount)}</span>
                            </div>
                          ))}
                          <div className="border-t pt-2 flex justify-between text-sm font-bold">
                            <span>Total Startup Costs</span>
                            <span>{formatCurrency(p.financial_projections.startup_costs_breakdown.reduce((a, b) => a + b.amount, 0))}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Revenue Projections */}
                    {p.financial_projections.revenue_projections && (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <h4 className="font-medium text-emerald-800 mb-3">Revenue Projections</h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-neutral-500">Year 1</p>
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(p.financial_projections.revenue_projections.year_1)}</p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-neutral-500">Year 2</p>
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(p.financial_projections.revenue_projections.year_2)}</p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-neutral-500">Year 3</p>
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(p.financial_projections.revenue_projections.year_3)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">Break-Even Analysis</h4>
                        <p className="text-sm text-neutral-700">{p.financial_projections.break_even_analysis}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-medium text-purple-800 mb-2">Funding Requirements</h4>
                        <p className="text-sm text-neutral-700">{p.financial_projections.funding_requirements}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Action Plan */}
            <TabsContent value="action">
              {p.action_plan && (
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Target className="w-5 h-5 mr-2 text-amber-500" />
                      Action Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {p.action_plan.immediate_actions?.length > 0 && (
                      <div className="p-4 bg-red-50 rounded-lg">
                        <h4 className="font-medium text-red-800 mb-2">Immediate Actions (First 30 Days)</h4>
                        <ul className="space-y-1">
                          {p.action_plan.immediate_actions.map((action, idx) => (
                            <li key={idx} className="text-sm text-neutral-700 flex items-start">
                              <span className="w-5 h-5 rounded-full bg-red-200 text-red-800 text-xs flex items-center justify-center mr-2 flex-shrink-0">{idx + 1}</span>
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {p.action_plan.short_term_goals?.length > 0 && (
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <h4 className="font-medium text-amber-800 mb-2">Short-Term Goals (First 6 Months)</h4>
                        <ul className="space-y-1">
                          {p.action_plan.short_term_goals.map((goal, idx) => (
                            <li key={idx} className="text-sm text-neutral-700 flex items-start">
                              <span className="w-5 h-5 rounded-full bg-amber-200 text-amber-800 text-xs flex items-center justify-center mr-2 flex-shrink-0">{idx + 1}</span>
                              {goal}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {p.action_plan.long_term_goals?.length > 0 && (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <h4 className="font-medium text-emerald-800 mb-2">Long-Term Goals (First 2 Years)</h4>
                        <ul className="space-y-1">
                          {p.action_plan.long_term_goals.map((goal, idx) => (
                            <li key={idx} className="text-sm text-neutral-700 flex items-start">
                              <span className="w-5 h-5 rounded-full bg-emerald-200 text-emerald-800 text-xs flex items-center justify-center mr-2 flex-shrink-0">{idx + 1}</span>
                              {goal}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Company Description */}
            <TabsContent value="company">
              {(p.company_description || p.vision || p.team) && (
                <div className="space-y-6">
                  {p.company_description && (
                    <Card><CardContent className="pt-6 space-y-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-500" />Company Description</h3>
                      {p.company_description.overview && <p className="text-sm text-neutral-700">{p.company_description.overview}</p>}
                      {p.company_description.products_services && <div className="p-3 bg-blue-50 rounded-lg"><h4 className="font-medium text-blue-800 text-sm mb-1">Products &amp; Services</h4><p className="text-sm text-neutral-700">{p.company_description.products_services}</p></div>}
                      {p.company_description.unique_value_proposition && <div className="p-3 bg-emerald-50 rounded-lg"><h4 className="font-medium text-emerald-800 text-sm mb-1">Unique Value Proposition</h4><p className="text-sm text-neutral-700">{p.company_description.unique_value_proposition}</p></div>}
                    </CardContent></Card>
                  )}
                  {p.vision && (
                    <Card><CardContent className="pt-6 space-y-3">
                      <h3 className="font-semibold text-lg">Vision</h3>
                      {p.vision.vision_statement && <p className="text-sm text-neutral-700 italic border-l-4 border-emerald-500 pl-4">{p.vision.vision_statement}</p>}
                      {p.vision.core_values?.length > 0 && <div><h4 className="font-medium text-sm mb-2">Core Values</h4><div className="flex flex-wrap gap-2">{p.vision.core_values.map((v, i) => <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">{v}</span>)}</div></div>}
                      {p.vision.long_term_goals && <p className="text-sm text-neutral-600">{p.vision.long_term_goals}</p>}
                    </CardContent></Card>
                  )}
                  {p.team && (
                    <Card><CardContent className="pt-6 space-y-3">
                      <h3 className="font-semibold text-lg">Team</h3>
                      {p.team.leadership && <p className="text-sm text-neutral-700">{p.team.leadership}</p>}
                      {p.team.key_roles?.length > 0 && <div className="grid sm:grid-cols-2 gap-2">{p.team.key_roles.map((r, i) => <div key={i} className="p-3 bg-neutral-50 rounded-lg"><p className="font-medium text-sm">{r.role}</p><p className="text-xs text-neutral-600">{r.responsibility}</p></div>)}</div>}
                      {p.team.hiring_plan && <p className="text-sm text-neutral-600">{p.team.hiring_plan}</p>}
                    </CardContent></Card>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Business Opportunity */}
            <TabsContent value="opportunity">
              {p.business_opportunity && (
                <Card><CardContent className="pt-6 space-y-4">
                  <h3 className="font-semibold text-lg">Business Opportunity</h3>
                  {["problem_statement", "solution", "market_gap", "growth_potential"].map(key => p.business_opportunity[key] && (
                    <div key={key} className="p-3 bg-neutral-50 rounded-lg">
                      <h4 className="font-medium text-sm capitalize mb-1">{key.replace(/_/g, " ")}</h4>
                      <p className="text-sm text-neutral-700">{p.business_opportunity[key]}</p>
                    </div>
                  ))}
                </CardContent></Card>
              )}
            </TabsContent>

            {/* Strategy (Marketing + Pricing) */}
            <TabsContent value="strategy">
              <div className="space-y-6">
                {p.pricing_strategy && (
                  <Card><CardContent className="pt-6 space-y-3">
                    <h3 className="font-semibold text-lg">Pricing Strategy</h3>
                    {p.pricing_strategy.model && <p className="text-sm text-neutral-700">{p.pricing_strategy.model}</p>}
                    {p.pricing_strategy.price_points?.length > 0 && <div className="space-y-2">{p.pricing_strategy.price_points.map((pp, i) => <div key={i} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"><span className="text-sm font-medium">{pp.product_service}</span><span className="text-sm text-emerald-600 font-semibold">{pp.price}</span></div>)}</div>}
                  </CardContent></Card>
                )}
                {(p.marketing_and_promotion || p.marketing_strategy) && (
                  <Card><CardContent className="pt-6 space-y-3">
                    <h3 className="font-semibold text-lg">Marketing &amp; Promotion</h3>
                    {(p.marketing_and_promotion || p.marketing_strategy)?.brand_positioning && <p className="text-sm text-neutral-700 italic border-l-4 border-blue-500 pl-4">{(p.marketing_and_promotion || p.marketing_strategy).brand_positioning}</p>}
                    {(p.marketing_and_promotion || p.marketing_strategy)?.marketing_channels?.length > 0 && <div className="space-y-2">{(p.marketing_and_promotion || p.marketing_strategy).marketing_channels.map((ch, i) => <div key={i} className="p-3 bg-blue-50 rounded-lg"><p className="font-medium text-sm text-blue-800">{ch.channel || ch}</p>{ch.strategy && <p className="text-xs text-neutral-600">{ch.strategy}</p>}</div>)}</div>}
                    {(p.marketing_and_promotion || p.marketing_strategy)?.launch_plan && <div className="p-3 bg-emerald-50 rounded-lg"><h4 className="font-medium text-sm text-emerald-800 mb-1">Launch Plan</h4><p className="text-sm text-neutral-700">{(p.marketing_and_promotion || p.marketing_strategy).launch_plan}</p></div>}
                  </CardContent></Card>
                )}
              </div>
            </TabsContent>

            {/* Milestones */}
            <TabsContent value="milestones">
              {(p.milestones_and_key_objectives || p.operations_plan?.milestones) && (
                <Card><CardContent className="pt-6 space-y-4">
                  <h3 className="font-semibold text-lg">Milestones &amp; Key Objectives</h3>
                  {p.milestones_and_key_objectives ? (
                    <div className="space-y-4">
                      {[{label: "Month 1-3", key: "month_1_3", color: "red"}, {label: "Month 4-6", key: "month_4_6", color: "amber"}, {label: "Month 7-12", key: "month_7_12", color: "blue"}, {label: "Year 2-3", key: "year_2_3", color: "emerald"}].map(phase => p.milestones_and_key_objectives[phase.key]?.length > 0 && (
                        <div key={phase.key} className={`border-l-4 border-${phase.color}-500 pl-4`}>
                          <h4 className="font-medium text-sm mb-2">{phase.label}</h4>
                          {p.milestones_and_key_objectives[phase.key].map((m, i) => <div key={i} className="mb-2"><p className="text-sm font-medium text-neutral-800">{m.milestone}</p>{m.kpi && <p className="text-xs text-neutral-500">KPI: {m.kpi}</p>}</div>)}
                        </div>
                      ))}
                    </div>
                  ) : p.operations_plan?.milestones?.map((m, i) => (
                    <div key={i} className="p-3 bg-neutral-50 rounded-lg flex items-center justify-between">
                      <span className="text-sm">{m.milestone}</span><span className="text-xs text-neutral-500">{m.timeline}</span>
                    </div>
                  ))}
                </CardContent></Card>
              )}
            </TabsContent>

            {/* Funding Required */}
            <TabsContent value="funding">
              {(p.funding_required || p.financial_projections?.funding_requirements) && (
                <Card><CardContent className="pt-6 space-y-4">
                  <h3 className="font-semibold text-lg">Funding Required</h3>
                  {p.funding_required ? (
                    <>
                      {p.funding_required.total_funding_needed && <div className="p-4 bg-emerald-50 rounded-lg text-center"><p className="text-2xl font-bold text-emerald-700">{p.funding_required.total_funding_needed}</p><p className="text-xs text-neutral-500">Total Funding Needed</p></div>}
                      {p.funding_required.use_of_funds?.length > 0 && <div><h4 className="font-medium text-sm mb-2">Use of Funds</h4>{p.funding_required.use_of_funds.map((f, i) => <div key={i} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg mb-1"><div><p className="text-sm font-medium">{f.category}</p><p className="text-xs text-neutral-500">{f.description}</p></div><span className="text-sm font-semibold text-emerald-600">${typeof f.amount === 'number' ? f.amount.toLocaleString() : f.amount}</span></div>)}</div>}
                      {p.funding_required.funding_sources && <p className="text-sm text-neutral-700"><span className="font-medium">Recommended Sources:</span> {p.funding_required.funding_sources}</p>}
                      {p.funding_required.roi_projection && <p className="text-sm text-neutral-700"><span className="font-medium">ROI Projection:</span> {p.funding_required.roi_projection}</p>}
                    </>
                  ) : (
                    <p className="text-sm text-neutral-700">{p.financial_projections?.funding_requirements}</p>
                  )}
                </CardContent></Card>
              )}
            </TabsContent>

          </Tabs>

          {/* Branding Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-neutral-400">Powered by My Biz Consulting - Business Solutions Center</p>
          </div>
          </>
        )}

        {/* Parse Error */}
        {p?.parse_error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-6">
              <p className="text-red-700 mb-4">There was an issue parsing the generated plan. Raw response:</p>
              <pre className="text-xs bg-white p-4 rounded overflow-auto max-h-96">{p.raw_response}</pre>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BusinessPlanPage;
